"use client";

import { useMemo } from 'react';
import Link from "next/link";
import Image from "next/image";
import Button from "../ui/Button";
import { SponsoredBanners } from "..";
import { usePageShowcases } from '../providers/PageShowcasesProvider';
import { getStoredStoreId } from '@/lib/store-storage';
import { mergeCannabisFieldsFromAvailability } from '@/lib/product-potency-merge';
import { PRODUCT_PLACEHOLDER_IMAGE } from '@shared/lib/product-placeholder';
import { generateProductSlug } from '@shared/lib/slugify';

function toCardItem(product: any, storeId: number | null) {
  let avail = Array.isArray(product?.availabilities) && product.availabilities.length > 0
    ? (storeId
        ? product.availabilities.find((a: any) => a.covaLocationId === storeId) || product.availabilities[0]
        : product.availabilities[0])
    : null;

  if (!avail) {
    const groupProducts = Array.isArray(product?.groupProducts) ? product.groupProducts : [];
    for (const gp of groupProducts) {
      const gpAvails = Array.isArray(gp?.availabilities) ? gp.availabilities : [];
      const found = storeId
        ? gpAvails.find((a: any) => a.covaLocationId === storeId && Number(a.inStock || 0) > 0)
        : gpAvails.find((a: any) => Number(a.inStock || 0) > 0);
      if (found) { avail = found; break; }
    }
  }

  if (!avail) {
    const sizeOptions = Array.isArray(product?.sizeOptions) ? product.sizeOptions : [];
    const inStockOption = sizeOptions.find((opt: any) => Number(opt.inStock || 0) >= 1);
    if (inStockOption) {
      avail = {
        packageId: inStockOption.packageId,
        price: inStockOption.price,
        compareAtPrice: inStockOption.compareAtPrice ?? null,
        unitCost: inStockOption.unitCost ?? null,
        inStock: inStockOption.inStock,
        covaLocationId: storeId,
      };
    }
  }

  if (!avail) return null;

  const price =
    avail.price !== null && avail.price !== undefined ? Number(avail.price) : Number(avail.unitCost || 0);

  const groupProducts = Array.isArray(product?.groupProducts) ? product.groupProducts : [];

  // Process sizeOptions - enhance with full details from groupProducts if needed
  let sizeOptions = Array.isArray(product?.sizeOptions)
    ? product.sizeOptions
        .map((opt: any) => {
          // Find matching product in groupProducts for additional details
          const p = groupProducts.find((gp: any) => 
            gp.id === opt.productId || 
            gp.productId === opt.productId ||
            (gp.product && (gp.product.id === opt.productId || gp.product.productId === opt.productId))
          );
          
          // Try to find availability for this product/option
          let pAvail = null;
          if (p?.availabilities) {
            pAvail = Array.isArray(p.availabilities) && p.availabilities.length > 0 
              ? (storeId 
                  ? p.availabilities.find((a: any) => a.covaLocationId === storeId) || p.availabilities[0]
                  : p.availabilities[0])
              : null;
          }
          
          // Get product details - handle both direct product object and nested product.product
          const pProduct = p?.product || p;
          
          return {
            packageId: opt.packageId || pAvail?.packageId || p?.packageId || avail.packageId,
            sizeOrWeight: opt.sizeOrWeight || p?.sizeOrWeight || null,
            price: typeof opt.price === 'number' ? opt.price : Number(opt.price || p?.price || 0),
            compareAtPrice: typeof opt.compareAtPrice === 'number' ? opt.compareAtPrice : (opt.compareAtPrice !== null && opt.compareAtPrice !== undefined ? Number(opt.compareAtPrice) : (pAvail?.compareAtPrice ? Number(pAvail.compareAtPrice) : null)),
            unitCost: typeof opt.unitCost === 'number' ? opt.unitCost : (opt.unitCost !== null && opt.unitCost !== undefined ? Number(opt.unitCost) : (pAvail?.unitCost ? Number(pAvail.unitCost) : null)),
            inStock: typeof opt.inStock === 'number' ? opt.inStock : Number(opt.inStock || pAvail?.inStock || p?.inStock || 0),
            productId: opt.productId || p?.productId || p?.id,
            variationId: opt.variationId || p?.variationId || null,
            productSlug: opt.productSlug || p?.slug || pProduct?.slug || product?.slug || null,
            heroShotUri: opt.heroShotUri || p?.heroShotUri || pProduct?.heroShotUri || product?.heroShotUri || null,
            shortDescription: opt.shortDescription || p?.shortDescription || pProduct?.shortDescription || product?.shortDescription || null,
            longDescription: opt.longDescription || p?.longDescription || pProduct?.longDescription || product?.longDescription || null,
            netContent: opt.netContent || p?.netContent || pProduct?.netContent || product?.netContent || null,
          };
        })
        .filter((x: any) => x.inStock >= 1)
    : [];

  // Enhance groupProducts with availability data for ProductCard variant parsing
  const enhancedGroupProducts = groupProducts.length > 0
    ? groupProducts.map((gp: any) => {
        const gpProduct = gp?.product || gp;
        let gpAvail = null;
        if (gp?.availabilities) {
          gpAvail = Array.isArray(gp.availabilities) && gp.availabilities.length > 0
            ? (storeId
                ? gp.availabilities.find((a: any) => a.covaLocationId === storeId) || gp.availabilities[0]
                : gp.availabilities[0])
            : null;
        }
        
        return {
          id: gp.id || gp.productId,
          productId: gp.productId || gp.id,
          name: gpProduct?.name || gp.name,
          slug: gpProduct?.slug || gp.slug,
          heroShotUri: gpProduct?.heroShotUri || gp.heroShotUri,
          brandName: gpProduct?.brandName || gp.brandName,
          classificationName: gpProduct?.classificationName || gp.classificationName,
          shortDescription: gpProduct?.shortDescription || gp.shortDescription,
          longDescription: gpProduct?.longDescription || gp.longDescription,
          sizeOrWeight: gp.sizeOrWeight || null,
          masterProductId: gpProduct?.masterProductId || gp.masterProductId,
          masterProductName: gpProduct?.masterProductName || gp.masterProductName,
          ...(gpAvail ? {
            availabilities: [gpAvail],
          } : {}),
        };
      })
    : [];

  // For merch/clothing/apparel/accessories: derive full sizeOptions from groupProducts
  const classification = product?.classificationName || '';
  const isMerch = classification && (
    classification.toLowerCase().includes('merch') ||
    classification.toLowerCase().includes('clothing') ||
    classification.toLowerCase().includes('apparel') ||
    classification.toLowerCase().includes('accessories')
  );

  if (isMerch && enhancedGroupProducts.length > 0 && sizeOptions.length === 0) {
    sizeOptions = enhancedGroupProducts
      .map((gp: any) => {
        const gpAvail = gp?.availabilities?.[0];
        if (!gpAvail || Number(gpAvail.inStock || 0) <= 0) return null;
        
        const gpPrice = gpAvail.price !== null && gpAvail.price !== undefined
          ? Number(gpAvail.price)
          : Number(gpAvail.unitCost || 0);
        
        return {
          packageId: gpAvail.packageId || gp.id,
          sizeOrWeight: gp.sizeOrWeight || null,
          price: Number.isFinite(gpPrice) ? gpPrice : 0,
          compareAtPrice: gpAvail.compareAtPrice ? Number(gpAvail.compareAtPrice) : null,
          unitCost: gpAvail.unitCost ? Number(gpAvail.unitCost) : null,
          inStock: Number(gpAvail.inStock || 0),
          productId: gp.productId || gp.id,
          variationId: gp.variationId || null,
          productSlug: gp.slug || null,
          heroShotUri: gp.heroShotUri || null,
          shortDescription: gp.shortDescription || null,
          longDescription: gp.longDescription || null,
          netContent: gp.netContent || null,
        };
      })
      .filter(Boolean) as any[];
  }

  // Prefer backend grouped display name
  const displayName = product?.name || product?.masterProductName || product?.classificationName || 'Product';
  const mergedRoot = mergeCannabisFieldsFromAvailability(product, avail);

  return {
    item: {
      id: product.id,
      productId: product.id,
      packageId: avail.packageId,
      inStock: Number(avail.inStock || 0),
      price: Number.isFinite(price) ? price : 0,
      compareAtPrice: avail.compareAtPrice ? Number(avail.compareAtPrice) : null,
      unitCost: avail.unitCost ? Number(avail.unitCost) : null,
      product: {
        id: product.id,
        productId: product.id,
        name: displayName,
        slug: product.slug || null,
        heroShotUri: product.heroShotUri || null,
        brandName: product.brandName || null,
        classificationName: product.classificationName || null,
        shortDescription: product.shortDescription || null,
        longDescription: product.longDescription || null,
        strain: mergedRoot.strain ?? null,
        thcMin: mergedRoot.thcMin ?? null,
        thcMax: mergedRoot.thcMax ?? null,
        thcUom: mergedRoot.thcUom ?? null,
        thcPercent: mergedRoot.thcPercent ?? null,
        thcMg: mergedRoot.thcMg ?? null,
        cbdMin: mergedRoot.cbdMin ?? null,
        cbdMax: mergedRoot.cbdMax ?? null,
        cbdUom: mergedRoot.cbdUom ?? null,
        cbdPercent: mergedRoot.cbdPercent ?? null,
        cbdMg: mergedRoot.cbdMg ?? null,
        netContent: product.netContent || null,
        pieces: product.pieces || null,
        equivalenceDriedFlowerG: product.equivalenceDriedFlowerG || null,
        sizeOptions,
        groupProducts: enhancedGroupProducts,
        availabilities: product.availabilities,
      },
    },
    price,
  };
}

export type RecentDealsSectionProps = {
  /** Sponsored banner position key (e.g. homepage `3`, same-day `sameday-delivery-3`, location `location-detail-1`). */
  sponsoredPosition?: string;
  /** Prefer banners scoped to this store when set. */
  covaLocationId?: number | null;
  /** Section horizontal padding (match surrounding page). */
  className?: string;
  /** Pink banner card copy; defaults to the homepage "Check our recent deals" treatment. */
  bannerHeading?: React.ReactNode;
  bannerDescription?: string;
  bannerCtaText?: string;
  /** Overrides the collection URL as the banner CTA destination. */
  bannerCtaHref?: string;
};

export default function RecentDealsSection({
  sponsoredPosition,
  covaLocationId = null,
  className = 'px-4 sm:px-6',
  bannerHeading,
  bannerDescription,
  bannerCtaText = 'View all deals',
  bannerCtaHref,
}: RecentDealsSectionProps) {
  const { slotsByKey, loading } = usePageShowcases();
  const slot = slotsByKey['recentDeals'];
  const resolvedStoreId =
    covaLocationId != null && Number.isFinite(Number(covaLocationId)) && Number(covaLocationId) > 0
      ? Number(covaLocationId)
      : getStoredStoreId();

  // Extract data for hooks (before early returns)
  const rawProducts = Array.isArray(slot?.products) ? slot.products : [];
  const collectionHandle = slot?.collectionHandle || null;

  // All hooks must be called before any early returns
  const mapped = useMemo(() => {
    if (!slot || !slot.collectionId || rawProducts.length === 0) return [];
    return rawProducts
      .map((p: any) => toCardItem(p, resolvedStoreId))
      .filter(Boolean) as Array<{ item: any; price: number }>;
  }, [rawProducts, resolvedStoreId, slot]);

  // Simply take first 4 products
  const filtered = useMemo(() => {
    if (mapped.length === 0) return [];
    return mapped.slice(0, 4);
  }, [mapped]);

  // Helper function to generate product URL (matching ProductCard logic)
  const getProductUrl = (product: { item: any; price: number }): string => {
    // Generate SEO-optimized slug from parsed product name (same for all sizes in a group)
    // Base slug should NOT include size/weight (same for all sizes in group)
    const displayName = product.item.product.name;
    const productNameSlug = generateProductSlug({
      name: displayName,
      id: product.item.productId,
      brandName: product.item.product.brandName || undefined,
      classificationName: product.item.product.classificationName || undefined,
      sizeOrWeight: null, // Base slug should NOT include size/weight (same for all sizes)
    });

    if (!productNameSlug) {
      return `/product/${product.item.productId}`;
    }

    // FIFO: Use the first size option's product slug (oldest in-stock lot)
    // Falls back to item.product.slug if no size option available
    const productSlug = product.item.sizeOptions?.[0]?.productSlug || product.item.product.slug;

    const params = new URLSearchParams();
    // Use variant parameter with exact product slug from COVA
    if (productSlug) {
      params.set("variant", productSlug);
    }

    const queryString = params.toString();
    return `/product/${productNameSlug}${queryString ? `?${queryString}` : ""}`;
  };

  // Generate collection URL for "View all deals" button
  const collectionUrl = useMemo(() => {
    if (collectionHandle) {
      return `/collections/${collectionHandle}`;
    }
    if (slot?.collectionId) {
      return `/search?collection=${encodeURIComponent(slot.collectionId)}`;
    }
    return "/search";
  }, [collectionHandle, slot?.collectionId]);

  // Early returns after all hooks
  if (loading) return null;
  if (!slot || !slot.collectionId) return null;

  // Take first 4 products for the deals layout
  const displayProducts = filtered.slice(0, 4);

  return (
    <section className={className} aria-label="Recent deals">
      <div className="w-full flex flex-col gap-4">
        {/* Row 1 - First 2 Products */}
        <div className="w-full flex flex-col lg:grid lg:grid-cols-[41%_1fr] gap-4">
          {displayProducts[0] && (
            <div className="bg-constant-white rounded-xl shadow-sm border border-neutral-10 p-4 flex max-sm:flex-col gap-3 transition-shadow duration-200">
              <div className="flex-shrink-0">
                <div className="w-[180px] max-sm:w-full h-[180px] bg-neutral-5 rounded-lg overflow-hidden flex items-center justify-center">
                  {displayProducts[0].item.product.heroShotUri ? (
                    <img
                      src={displayProducts[0].item.product.heroShotUri}
                      alt={displayProducts[0].item.product.name}
                      className="w-full h-full object-contain"
                      loading="lazy"
                      decoding="async"
                      fetchPriority="low"
                      onError={(e) => {
                        const el = e.currentTarget as HTMLImageElement;
                        if (!el.src.startsWith("data:image/svg+xml")) {
                          el.src = PRODUCT_PLACEHOLDER_IMAGE;
                          el.className = "w-full h-full object-contain opacity-30";
                        }
                      }}
                    />
                  ) : (
                    <Image
                      src={PRODUCT_PLACEHOLDER_IMAGE}
                      alt="Product placeholder"
                      width={180}
                      height={180}
                      className="w-full h-full object-contain opacity-30"
                    />
                  )}
                </div>
              </div>
              <div className="flex-1 flex flex-col justify-between min-w-0">
                <div>
                  <h3 className="text-lg text-text-primary font-medium mb-2 leading-tight">
                    {displayProducts[0].item.product.name}
                  </h3>
                  <p className="sm:text-body-md text-body-14-mobile text-text-secondary font-medium leading-relaxed line-clamp-2">
                    {displayProducts[0].item.product.shortDescription || displayProducts[0].item.product.longDescription || ''}
                  </p>
                </div>
                <Button asChild variant="bordered" color="white" size="lg" className="rounded-2xl max-sm:mt-2 max-sm:w-full px-4 py-3 w-fit">
                  <Link href={getProductUrl(displayProducts[0])}>Learn more</Link>
                </Button>
              </div>
            </div>
          )}

          {displayProducts[1] && (
            <div className="bg-constant-white rounded-xl shadow-sm border border-neutral-10 p-4 flex max-sm:flex-col gap-3 transition-shadow duration-200">
              <div className="flex-shrink-0">
                <div className="w-[180px] max-sm:w-full h-[180px] bg-neutral-5 rounded-lg overflow-hidden flex items-center justify-center">
                  {displayProducts[1].item.product.heroShotUri ? (
                    <img
                      src={displayProducts[1].item.product.heroShotUri}
                      alt={displayProducts[1].item.product.name}
                      className="w-full h-full object-contain"
                      loading="lazy"
                      decoding="async"
                      fetchPriority="low"
                      onError={(e) => {
                        const el = e.currentTarget as HTMLImageElement;
                        if (!el.src.startsWith("data:image/svg+xml")) {
                          el.src = PRODUCT_PLACEHOLDER_IMAGE;
                          el.className = "w-full h-full object-contain opacity-30";
                        }
                      }}
                    />
                  ) : (
                    <Image
                      src={PRODUCT_PLACEHOLDER_IMAGE}
                      alt="Product placeholder"
                      width={180}
                      height={180}
                      className="w-full h-full object-contain opacity-30"
                    />
                  )}
                </div>
              </div>
              <div className="flex-1 flex flex-col justify-between min-w-0">
                <div>
                  <h3 className="text-lg text-text-primary font-medium mb-2 leading-tight">
                    {displayProducts[1].item.product.name}
                  </h3>
                  <p className="sm:text-body-md text-body-14-mobile text-text-secondary font-medium leading-relaxed line-clamp-2">
                    {displayProducts[1].item.product.shortDescription || displayProducts[1].item.product.longDescription || ''}
                  </p>
                </div>
                <Button asChild variant="bordered" color="white" size="lg" className="rounded-2xl max-sm:mt-2 max-sm:w-full px-4 py-3 w-fit">
                  <Link href={getProductUrl(displayProducts[1])}>Learn more</Link>
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Row 2 - Next 2 Products + Banner */}
        <div className="w-full flex flex-col lg:grid lg:grid-cols-[37%_37%_1fr] gap-4">
          {displayProducts[2] && (
            <div className="bg-constant-white rounded-xl shadow-sm border border-neutral-10 p-4 flex max-sm:flex-col gap-3 transition-shadow duration-200">
              <div className="flex-shrink-0">
                <div className="w-[180px] max-sm:w-full h-[180px] bg-neutral-5 rounded-lg overflow-hidden flex items-center justify-center">
                  {displayProducts[2].item.product.heroShotUri ? (
                    <img
                      src={displayProducts[2].item.product.heroShotUri}
                      alt={displayProducts[2].item.product.name}
                      className="w-full h-full object-contain"
                      loading="lazy"
                      decoding="async"
                      fetchPriority="low"
                      onError={(e) => {
                        const el = e.currentTarget as HTMLImageElement;
                        if (!el.src.startsWith("data:image/svg+xml")) {
                          el.src = PRODUCT_PLACEHOLDER_IMAGE;
                          el.className = "w-full h-full object-contain opacity-30";
                        }
                      }}
                    />
                  ) : (
                    <Image
                      src={PRODUCT_PLACEHOLDER_IMAGE}
                      alt="Product placeholder"
                      width={180}
                      height={180}
                      className="w-full h-full object-contain opacity-30"
                    />
                  )}
                </div>
              </div>
              <div className="flex-1 flex flex-col justify-between min-w-0">
                <div>
                  <h3 className="text-lg text-text-primary font-medium mb-2 leading-tight">
                    {displayProducts[2].item.product.name}
                  </h3>
                  <p className="sm:text-body-md text-body-14-mobile text-text-secondary font-medium leading-relaxed line-clamp-2">
                    {displayProducts[2].item.product.shortDescription || displayProducts[2].item.product.longDescription || ''}
                  </p>
                </div>
                <Button asChild variant="bordered" color="white" size="lg" className="rounded-2xl max-sm:mt-2 max-sm:w-full px-4 py-3 w-fit">
                  <Link href={getProductUrl(displayProducts[2])}>Learn more</Link>
                </Button>
              </div>
            </div>
          )}

          {displayProducts[3] && (
            <div className="bg-constant-white rounded-xl shadow-sm border border-neutral-10 p-4 flex max-sm:flex-col gap-3 transition-shadow duration-200">
              <div className="flex-shrink-0">
                <div className="w-[180px] max-sm:w-full h-[180px] bg-neutral-5 rounded-lg overflow-hidden flex items-center justify-center">
                  {displayProducts[3].item.product.heroShotUri ? (
                    <img
                      src={displayProducts[3].item.product.heroShotUri}
                      alt={displayProducts[3].item.product.name}
                      className="w-full h-full object-contain"
                      loading="lazy"
                      decoding="async"
                      fetchPriority="low"
                      onError={(e) => {
                        const el = e.currentTarget as HTMLImageElement;
                        if (!el.src.startsWith("data:image/svg+xml")) {
                          el.src = PRODUCT_PLACEHOLDER_IMAGE;
                          el.className = "w-full h-full object-contain opacity-30";
                        }
                      }}
                    />
                  ) : (
                    <Image
                      src={PRODUCT_PLACEHOLDER_IMAGE}
                      alt="Product placeholder"
                      width={180}
                      height={180}
                      className="w-full h-full object-contain opacity-30"
                    />
                  )}
                </div>
              </div>
              <div className="flex-1 flex flex-col justify-between min-w-0">
                <div>
                  <h3 className="text-lg text-text-primary font-medium mb-2 leading-tight">
                    {displayProducts[3].item.product.name}
                  </h3>
                  <p className="sm:text-body-md text-body-14-mobile text-text-secondary font-medium leading-relaxed line-clamp-2">
                    {displayProducts[3].item.product.shortDescription || displayProducts[3].item.product.longDescription || ''}
                  </p>
                </div>
                <Button asChild variant="bordered" color="white" size="lg" className="rounded-2xl max-sm:mt-2 max-sm:w-full px-4 py-3 w-fit">
                  <Link href={getProductUrl(displayProducts[3])}>Learn more</Link>
                </Button>
              </div>
            </div>
          )}

          {/* Banner Card */}
          <div className="bg-[#FFD2D2] rounded-xl shadow-sm border border-neutral-10 sm:p-6 p-4 flex flex-col justify-between relative overflow-hidden">
            {/* Background Shape */}
            <div className="absolute inset-0">
              <Image
                src="/media/sections/recent-deals/backgrounds/background-shape.webp"
                alt=""
                fill
                className="object-cover"
                aria-hidden="true"
              />
            </div>
            <div className="relative z-10 flex flex-col justify-between h-full">
              <div>
                <h3 className="sm:text-3xl text-h3-mobile font-medium text-text-primary leading-tight">
                  {bannerHeading ?? (
                    <>
                      Check our <br /> recent deals
                    </>
                  )}
                </h3>
                {bannerDescription ? (
                  <p className="mt-2 text-body-16 font-medium text-text-secondary leading-relaxed">
                    {bannerDescription}
                  </p>
                ) : null}
              </div>
              <Button asChild variant="primary" size="md" className="rounded-lg max-sm:mt-8 px-4 w-fit">
                <Link href={bannerCtaHref ?? collectionUrl}>{bannerCtaText}</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Sponsored banner, position configured in admin. No slot named, no
          banner: this used to default to the homepage's position 3, so the
          delivery pages showed the homepage's banner inside Recent Deals
          even though their admin pages never declared one there. */}
      {sponsoredPosition ? (
        <div className="mt-8">
          <SponsoredBanners
            className="w-full"
            position={sponsoredPosition}
            covaLocationId={covaLocationId}
          />
        </div>
      ) : null}
    </section>
  );
}
